<script setup lang="ts">
/**
 * Beneficial Owner Letters (Appendix) 문서 내 [[변수]] 자리.
 *   · combo(race/gender/nationality) → CONTACT MY / MBRS 콤보
 *   · date → 인라인 + 📅
 *   · person/entity → 인라인 + ✨ (챗봇 lookup)
 *   · address → 인라인 + 📍
 *   · text → 인라인 직접 편집
 */
import { computed, inject, ref, onMounted, watch, nextTick } from 'vue';
import { BO_APPENDIX_DOC_CTX } from './boAppendixContext';

const props = defineProps<{ fieldKey: string; placeholder?: string; label?: string; compact?: boolean }>();
const ctx = inject(BO_APPENDIX_DOC_CTX)!;

const value = computed(() => ctx.valueOf(props.fieldKey));
const label = computed(() => props.placeholder || props.label || ctx.labelOf(props.fieldKey));
const fieldType = computed(() => ctx.typeOf(props.fieldKey));
const isDate = computed(() => fieldType.value === 'date');
const isAddress = computed(() => fieldType.value === 'address');
const isCombo = computed(() => fieldType.value === 'combo');
const isNationality = computed(() => /_nationality$/i.test(props.fieldKey));
const showIcon = computed(() => ['person', 'entity', 'date', 'address'].includes(fieldType.value));
const active = computed(() => ctx.activeKey.value === props.fieldKey);

function onIcon() {
  if (isAddress.value) ctx.openAddress(props.fieldKey);
  else ctx.activate(props.fieldKey);
}
const iconName = computed(() => (
  isDate.value ? 'mdi-calendar-month'
    : isAddress.value ? 'mdi-map-marker-outline'
      : 'mdi-creation'
));
const iconClass = computed(() => (
  isDate.value ? 'ic-date'
    : isAddress.value ? 'ic-addr'
      : 'ic-ai'
));
const iconTip = computed(() => (
  isDate.value ? 'Pick a date'
    : isAddress.value ? 'Enter address'
      : 'Look up from chat'
));

const comboItems = computed(() => ctx.comboOptionsOf(props.fieldKey));
function onCombo(v: any) { ctx.setFieldValue(props.fieldKey, v ? String(v) : ''); }

const ed = ref<HTMLElement>();
onMounted(() => { if (ed.value) ed.value.textContent = value.value; });
watch(value, async (v) => {
  await nextTick();
  if (ed.value && document.activeElement !== ed.value && ed.value.textContent !== v) ed.value.textContent = v;
});
function onInput() {
  if (!ed.value) return;
  const txt = ed.value.textContent || '';
  if (!txt && ed.value.innerHTML) ed.value.innerHTML = '';
  ctx.setFieldValue(props.fieldKey, txt);
}
function clearField() {
  ctx.setFieldValue(props.fieldKey, '');
  if (ed.value) ed.value.innerHTML = '';
}

const dragOver = ref(false);
function onDrop(e: DragEvent) {
  dragOver.value = false;
  let v = '';
  const json = e.dataTransfer?.getData('application/x-entity-field');
  if (json) { try { v = String(JSON.parse(json).value ?? ''); } catch { /* noop */ } }
  if (!v) v = e.dataTransfer?.getData('text/plain') || '';
  if (v) ctx.setFieldValue(props.fieldKey, v.trim());
}

const placeable = computed(() => !!ctx.pendingPickValue.value);
function onFieldMousedown(e: MouseEvent) {
  if (!ctx.pendingPickValue.value) return;
  e.preventDefault();
  ctx.placePick(props.fieldKey);
}
</script>

<template>
  <v-select
    v-if="isCombo"
    class="bonf-combo" :class="{ filled: !!value, 'bonf-combo--wide': isNationality }"
    :model-value="value || null"
    :items="comboItems" item-title="label" item-value="value"
    :placeholder="label" variant="outlined" density="compact" hide-details clearable
    :menu-props="{ maxHeight: 280 }"
    @update:model-value="onCombo"
    @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop"
  />

  <span
    v-else
    class="bonf" :class="{ empty: !value, filled: !!value, active, dragover: dragOver, placeable, compact: props.compact }"
    @mousedown.capture="onFieldMousedown"
    @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop"
  >
    <span
      ref="ed" class="bonf-in" :data-ph="props.compact ? '' : label" contenteditable="true" spellcheck="false"
      @input="onInput" @keydown.enter.prevent="ed?.blur()"
    />
    <button
      v-if="showIcon"
      class="bonf-ic" :class="iconClass" :title="iconTip"
      @mousedown.prevent @click.stop="onIcon"
    >
      <v-icon size="12">{{ iconName }}</v-icon>
    </button>
    <button
      v-if="!!value"
      class="bonf-clear" title="Clear"
      @mousedown.prevent @click.stop="clearField"
    >
      <v-icon size="11">mdi-close</v-icon>
    </button>
  </span>
</template>

<style scoped>
.bonf { display:inline-flex; align-items:center; gap:3px; border-radius:4px; padding:0 3px; vertical-align:baseline; }
.bonf.empty { background:#fff6d6; box-shadow:inset 0 -2px 0 #f2c94c; }
.bonf.filled { background:#eaf3ff; box-shadow:inset 0 -2px 0 #1B84FF; }
.bonf.active { outline:2px solid #1B84FF; outline-offset:1px; }
.bonf.dragover { outline:2px dashed #22a06b; background:#e6f7ee; }
.bonf.placeable { outline:1.5px dashed #7bb0ff; cursor:copy; }
.bonf.placeable:hover { background:#eaf3ff; }
.bonf-in { min-width:46px; outline:none; font:inherit; color:#10305c; white-space:nowrap; }
.bonf.compact .bonf-in { min-width:18px; }
.bonf.filled .bonf-in { font-weight:600; }
.bonf-in:empty::before { content:attr(data-ph); color:#9a7b1a; font-style:italic; font-size:.9em; cursor:text; }
.bonf.compact .bonf-in:empty::before { content:''; }
.bonf-ic { display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px; border-radius:50%; border:none; cursor:pointer; flex:0 0 auto; padding:0; }
.bonf-ic.ic-ai { background:#eaf3ff; color:#1B84FF; }
.bonf-ic.ic-date { background:#eafaf1; color:#22a06b; }
.bonf-ic.ic-addr { background:#fdeefe; color:#a23bb0; }
.bonf-ic:hover { filter:brightness(.94); }
.bonf-clear { display:none; align-items:center; justify-content:center; width:15px; height:15px; border-radius:50%; border:none; background:#c4ccd6; color:#fff; cursor:pointer; flex:0 0 auto; padding:0; }
.bonf:hover .bonf-clear { display:inline-flex; }
.bonf-clear:hover { background:#8b95a1; }

.bonf-combo { display:inline-flex; min-width:140px; max-width:220px; vertical-align:middle; font-size:12px; line-height:1.3; }
.bonf-combo--wide { min-width:180px; max-width:280px; }
.bonf-combo :deep(.v-field) { cursor:pointer; border-radius:6px; background:#fff6d6; min-height:26px; font-size:12px; }
.bonf-combo.filled :deep(.v-field) { background:#eaf3ff; }
.bonf-combo :deep(.v-field__field) { min-height:26px; }
.bonf-combo :deep(.v-field__input) { min-height:26px; padding:0 6px; font-size:12px; line-height:1.3; color:#10305c; align-items:center; flex-wrap:nowrap; overflow:visible; }
.bonf-combo :deep(.v-select__selection) { max-width:none; overflow:visible; }
.bonf-combo :deep(.v-field__input),
.bonf-combo :deep(.v-select__selection),
.bonf-combo :deep(.v-select__selection-text),
.bonf-combo :deep(input) { font-size:12px !important; line-height:1.3; letter-spacing:normal; }
.bonf-combo.filled :deep(.v-select__selection-text),
.bonf-combo.filled :deep(.v-field__input) { font-weight:600; }
.bonf-combo :deep(input::placeholder) { color:#9a7b1a; font-style:italic; opacity:1; font-size:12px; }
.bonf-combo :deep(.v-field__append-inner) { padding-top:2px; }
.bonf-combo :deep(.v-field__append-inner .v-icon) { font-size:16px; }
.bonf-combo :deep(.v-field__outline) { --v-field-border-opacity:0.25; }
</style>
